import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { UploadCloud, Film, Music, X, Undo2, Redo2, RotateCcw, Download, Loader2, XCircle, Info, Clapperboard, Images } from "lucide-react";
import { toast } from "sonner";
import MusicLibrary from "./MusicLibrary";
import { SavedMusic } from "./SavedMusic";
import PhotoAlbumMaker from "./PhotoAlbumMaker";
import { chunkedUpload, downloadFile } from "./upload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtTime = (s) => {
  s = Math.max(0, s || 0);
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};
const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

const defaultSettings = (videoDur = 0, musicDur = 0) => ({
  video_start: 0, video_end: videoDur,
  music_start: 0, music_end: musicDur,
  music_offset: 0,
  music_volume: 0.8, original_volume: 1.0,
  original_mode: "mix",
  fade_in: 1, fade_out: 2,
  loop_music: true, duck_music: false,
  resolution: "original", aspect: "original",
});

export default function AddMusicToVideo() {
  const [mode, setMode] = useState("video"); // video | album
  const [limits, setLimits] = useState(null);
  const [video, setVideo] = useState(null);        // {file_id, filename, duration, size, has_audio, width, height}
  const [music, setMusic] = useState(null);        // {source: 'upload'|'library', file_id?, track_id?, title, duration}
  const [uploading, setUploading] = useState(null); // {kind, pct}
  const [settings, setSettings] = useState(defaultSettings());
  const [history, setHistory] = useState({ past: [], future: [] });
  const [job, setJob] = useState(null);            // {id, status, progress, error}
  const [outputName, setOutputName] = useState("video-with-music");
  const [dragOver, setDragOver] = useState(false);
  const [curTime, setCurTime] = useState(0);
  const [musicRefresh, setMusicRefresh] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const videoRef = useRef(null);
  const musicRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/funtime/limits`).then((r) => setLimits(r.data)).catch(() => {});
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (musicRef.current) musicRef.current.pause();
    };
  }, []);

  // ---------- settings history (undo / redo / reset) ----------
  const applySettings = (updater) => {
    setSettings((prev) => {
      const next = typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      setHistory((h) => ({ past: [...h.past.slice(-30), prev], future: [] }));
      return next;
    });
  };
  const undo = () => setHistory((h) => {
    if (!h.past.length) return h;
    const prev = h.past[h.past.length - 1];
    setSettings((cur) => { h._redo = cur; return prev; });
    return { past: h.past.slice(0, -1), future: [...h.future, h._redo] };
  });
  const redo = () => setHistory((h) => {
    if (!h.future.length) return h;
    const nxt = h.future[h.future.length - 1];
    setSettings(() => nxt);
    return { past: [...h.past, settings], future: h.future.slice(0, -1) };
  });
  const reset = () => {
    setHistory({ past: [], future: [] });
    setSettings(defaultSettings(video?.duration || 0, music?.duration || 0));
    toast.message("Settings reset to defaults");
  };

  // ---------- uploads ----------
  const handleVideoFile = async (file) => {
    if (!file) return;
    setUploading({ kind: "video", pct: 0 });
    try {
      const meta = await chunkedUpload(file, "video", (pct) => setUploading({ kind: "video", pct }));
      setVideo(meta);
      setSettings(defaultSettings(meta.duration, music?.duration || 0));
      setHistory({ past: [], future: [] });
      if (!meta.has_audio) toast.message("This video has no audio track — only the music will be heard.");
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Video upload failed — please retry");
    } finally {
      setUploading(null);
    }
  };

  const handleMusicFile = async (file) => {
    if (!file) return;
    setUploading({ kind: "audio", pct: 0 });
    try {
      const meta = await chunkedUpload(file, "audio", (pct) => setUploading({ kind: "audio", pct }));
      setMusic({ source: "upload", file_id: meta.file_id, title: meta.filename, duration: meta.duration });
      applySettings({ music_start: 0, music_end: meta.duration, music_offset: 0 });
      setMusicRefresh((x) => x + 1);
      toast.success("Music uploaded — it stays in “My saved music” for future projects");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Audio upload failed — please retry");
    } finally {
      setUploading(null);
    }
  };

  const selectLibraryTrack = (t) => {
    setMusic({ source: "library", track_id: t.id, title: t.title, duration: t.duration });
    applySettings({ music_start: 0, music_end: t.duration, music_offset: 0 });
    toast.success(`"${t.title}" selected`);
  };

  const removeMusic = () => {
    setMusic(null);
    if (musicRef.current) { musicRef.current.pause(); musicRef.current = null; }
  };

  // ---------- synced preview ----------
  const musicUrl = music
    ? (music.source === "library"
        ? `${API}/funtime/music-library/${music.track_id}/audio`
        : `${API}/funtime/file/${music.file_id}`)
    : null;

  const syncMusic = useCallback(() => {
    const v = videoRef.current, m = musicRef.current;
    if (!v || !m || !music) return;
    const s = settings;
    const t = v.currentTime;
    setCurTime(t);
    if (t < s.video_start - 0.05) v.currentTime = s.video_start;
    if (t >= s.video_end - 0.02 && s.video_end > 0) { v.pause(); m.pause(); return; }
    v.volume = s.original_mode === "mute" ? 0 : Math.min(1, s.original_volume);
    const rel = t - s.video_start - s.music_offset;
    const segLen = Math.max(0.1, (s.music_end || music.duration) - s.music_start);
    const used = Math.max(0.1, (s.video_end - s.video_start) - s.music_offset);
    const active = rel >= 0 && (s.loop_music ? rel < used : rel < Math.min(segLen, used));
    if (!active) { if (!m.paused) m.pause(); return; }
    const pos = s.music_start + (s.loop_music ? rel % segLen : rel);
    if (Math.abs(m.currentTime - pos) > 0.35) m.currentTime = pos;
    let fade = 1;
    const totalUsed = s.loop_music ? used : Math.min(segLen, used);
    if (s.fade_in > 0 && rel < s.fade_in) fade = rel / s.fade_in;
    if (s.fade_out > 0 && rel > totalUsed - s.fade_out) fade = Math.min(fade, Math.max(0, (totalUsed - rel) / s.fade_out));
    m.volume = Math.min(1, Math.max(0, s.music_volume * fade));
    if (!v.paused && m.paused) m.play().catch(() => {});
  }, [settings, music]);

  useEffect(() => {
    const id = setInterval(syncMusic, 120);
    return () => clearInterval(id);
  }, [syncMusic]);

  const onVideoPlay = () => { syncMusic(); };
  const onVideoPause = () => { if (musicRef.current) musicRef.current.pause(); };

  // ---------- export ----------
  const startExport = async () => {
    if (!video) return;
    try {
      const body = {
        video_file_id: video.file_id,
        music: music ? { source: music.source, file_id: music.file_id, track_id: music.track_id } : { source: "none" },
        settings,
        output_name: outputName,
      };
      const res = await axios.post(`${API}/funtime/export`, body);
      const jobId = res.data.job_id;
      setJob({ id: jobId, status: "processing", progress: 0 });
      pollRef.current = setInterval(async () => {
        try {
          const st = await axios.get(`${API}/funtime/export/${jobId}/status`);
          setJob({ id: jobId, ...st.data });
          if (["done", "error", "cancelled"].includes(st.data.status)) {
            clearInterval(pollRef.current);
            if (st.data.status === "done") toast.success("Export finished — ready to download!");
            if (st.data.status === "error") toast.error("Export failed — you can adjust settings and retry");
          }
        } catch { /* transient poll error */ }
      }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start export");
    }
  };

  const cancelExport = async () => {
    if (!job) return;
    try { await axios.post(`${API}/funtime/export/${job.id}/cancel`); } catch { /* ignore */ }
    setJob(null);
    if (pollRef.current) clearInterval(pollRef.current);
  };

  const downloadExport = async () => {
    setDownloading(true);
    try {
      await downloadFile(`${API}/funtime/export/${job.id}/download`, `${outputName || "video"}.mp4`);
    } catch {
      toast.error("Download failed — please try again");
    } finally {
      setDownloading(false);
    }
  };

  const set = (k) => (v) => applySettings({ [k]: Array.isArray(v) ? v[0] : v });
  const canExport = video && !uploading && (!job || ["done", "error", "cancelled"].includes(job.status));
  const musicSegLen = music ? Math.max(0, (settings.music_end || music.duration) - settings.music_start) : 0;

  const SliderRow = ({ label, value, min, max, step, onChange, fmt = (x) => x, testId }) => (
    <div className="space-y-1">
      <div className="flex justify-between font-mono text-[10px] uppercase text-muted-foreground">
        <span>{label}</span><span className="text-card-foreground">{fmt(value)}</span>
      </div>
      <Slider value={[value]} min={min} max={max} step={step} onValueChange={onChange} data-testid={testId} />
    </div>
  );

  const musicCard = (
    <Card className="border-border/60">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="font-mono text-xs font-bold text-primary">2 · Choose music</p>
          {music && (
            <button onClick={removeMusic} className="font-mono text-[10px] text-rose-400 flex items-center gap-1" data-testid="music-remove"><X className="w-3 h-3" />Remove</button>
          )}
        </div>
        {music && (
          <div className="flex items-center gap-2 rounded-lg border border-primary/60 bg-primary/10 px-3 py-2 font-mono text-xs" data-testid="music-selected">
            <Music className="w-4 h-4 text-primary" />
            <span className="truncate font-bold">{music.title}</span>
            <span className="text-[10px] text-muted-foreground ml-auto">{fmtTime(music.duration)}</span>
          </div>
        )}
        <label className="inline-flex items-center gap-2 font-mono text-[11px] text-sky-300 cursor-pointer hover:underline">
          <UploadCloud className="w-3.5 h-3.5" />
          {music ? "Replace with my own file (MP3/WAV/M4A)" : "Upload my own music (MP3/WAV/M4A)"}
          <input type="file" accept="audio/mpeg,audio/wav,audio/mp4,.mp3,.wav,.m4a,.aac,.ogg" className="hidden" onChange={(e) => handleMusicFile(e.target.files?.[0])} data-testid="music-file-input" />
        </label>
        {uploading?.kind === "audio" && (
          <div className="h-2 rounded bg-secondary overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${uploading.pct}%` }} data-testid="music-upload-progress" /></div>
        )}
        <SavedMusic
          selectedFileId={music?.source === "upload" ? music.file_id : null}
          refreshKey={musicRefresh}
          onSelect={(tr) => {
            setMusic({ source: "upload", file_id: tr.file_id, title: tr.filename, duration: tr.duration });
            applySettings({ music_start: 0, music_end: tr.duration, music_offset: 0 });
            toast.success(`"${tr.filename}" selected`);
          }}
        />
        <p className="font-mono text-[10px] uppercase text-muted-foreground pt-1">or pick from the built-in library</p>
        <MusicLibrary selectedTrackId={music?.track_id} onSelect={selectLibraryTrack} />
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="add-music-tool">
      {limits && (
        <div className="bg-sky-950/60 border border-sky-500/50 rounded-lg px-3 py-2 flex items-start gap-2">
          <Info className="w-3.5 h-3.5 text-sky-300 mt-0.5 flex-shrink-0" />
          <p className="font-mono text-[10px] text-sky-100" data-testid="amv-limits">
            Video up to {limits.max_video_mb} MB / {limits.max_video_minutes} min ({limits.video_formats.join(", ").toUpperCase()}) · Music up to {limits.max_audio_mb} MB ({limits.audio_formats.join(", ").toUpperCase()}) · Photos up to {limits.max_image_mb} MB each, {limits.max_photos} max ({(limits.image_formats || []).join(", ").toUpperCase()}) · Media inside an album stays saved; loose files auto-delete after {limits.file_ttl_hours}h · Uploaded music is always kept for future projects · Preview plays video + music together; the export matches it.
          </p>
        </div>
      )}

      {/* Mode toggle: single video vs photo album */}
      <div className="flex gap-1.5">
        <Button size="sm" variant={mode === "video" ? "default" : "outline"} className="h-8 font-mono text-xs" onClick={() => setMode("video")} data-testid="mode-video">
          <Clapperboard className="w-3.5 h-3.5 mr-1.5" /> Video + Music
        </Button>
        <Button size="sm" variant={mode === "album" ? "default" : "outline"} className="h-8 font-mono text-xs" onClick={() => setMode("album")} data-testid="mode-album">
          <Images className="w-3.5 h-3.5 mr-1.5" /> Albums (photos + videos)
        </Button>
      </div>

      {mode === "album" ? (
        <PhotoAlbumMaker music={music} musicUrl={musicUrl} musicSlot={musicCard} />
      ) : (
      <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* STEP 1 — video upload */}
        <Card className="border-border/60">
          <CardContent className="p-4 space-y-3">
            <p className="font-mono text-xs font-bold text-primary">1 · Upload video</p>
            {!video ? (
              <label
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); handleVideoFile(e.dataTransfer.files?.[0]); }}
                className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-10 cursor-pointer transition-colors ${dragOver ? "border-primary bg-primary/10" : "border-border/70 hover:border-primary/60"}`}
                data-testid="video-dropzone"
              >
                <UploadCloud className="w-8 h-8 text-primary" />
                <span className="font-mono text-xs text-card-foreground">Drag &amp; drop a video, or tap to choose</span>
                <span className="font-mono text-[10px] text-muted-foreground">MP4 · MOV · WebM</span>
                <input type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm,.m4v" className="hidden" onChange={(e) => handleVideoFile(e.target.files?.[0])} data-testid="video-file-input" />
              </label>
            ) : (
              <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-secondary/30 px-3 py-2" data-testid="video-info">
                <Film className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="min-w-0 flex-1 font-mono text-xs">
                  <p className="truncate font-bold text-card-foreground">{video.filename}</p>
                  <p className="text-[10px] text-muted-foreground">{fmtTime(video.duration)} · {fmtSize(video.size)} · {video.width}x{video.height} · {video.has_audio ? "has audio" : "silent"}</p>
                </div>
                <button onClick={() => { setVideo(null); setJob(null); }} className="p-1 text-rose-400" title="Remove video" data-testid="video-remove"><X className="w-4 h-4" /></button>
              </div>
            )}
            {uploading?.kind === "video" && (
              <div className="h-2 rounded bg-secondary overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${uploading.pct}%` }} data-testid="video-upload-progress" /></div>
            )}
          </CardContent>
        </Card>

        {/* STEP 2 — music */}
        {musicCard}
      </div>

      {video && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* STEP 3 — edit */}
          <Card className="border-border/60">
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <p className="font-mono text-xs font-bold text-primary">3 · Edit</p>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-7 px-2" onClick={undo} disabled={!history.past.length} title="Undo" data-testid="amv-undo"><Undo2 className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" className="h-7 px-2" onClick={redo} disabled={!history.future.length} title="Redo" data-testid="amv-redo"><Redo2 className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" className="h-7 px-2" onClick={reset} title="Reset" data-testid="amv-reset"><RotateCcw className="w-3.5 h-3.5" /></Button>
                </div>
              </div>

              {/* video trim timeline */}
              <div className="space-y-1">
                <div className="flex justify-between font-mono text-[10px] uppercase text-muted-foreground">
                  <span>Video trim</span>
                  <span className="text-card-foreground">{fmtTime(settings.video_start)} → {fmtTime(settings.video_end)}</span>
                </div>
                <Slider value={[settings.video_start, settings.video_end]} min={0} max={video.duration} step={0.1}
                  onValueChange={(v) => applySettings({ video_start: v[0], video_end: v[1] })} data-testid="video-trim-slider" />
              </div>

              {music && (
                <>
                  <div className="space-y-1">
                    <div className="flex justify-between font-mono text-[10px] uppercase text-muted-foreground">
                      <span>Music section</span>
                      <span className="text-card-foreground">{fmtTime(settings.music_start)} → {fmtTime(settings.music_end)} ({fmtTime(musicSegLen)})</span>
                    </div>
                    <Slider value={[settings.music_start, settings.music_end]} min={0} max={music.duration} step={0.1}
                      onValueChange={(v) => applySettings({ music_start: v[0], music_end: v[1] })} data-testid="music-trim-slider" />
                  </div>
                  <SliderRow label="Music starts at (in video)" value={settings.music_offset} min={0}
                    max={Math.max(0, settings.video_end - settings.video_start)} step={0.1}
                    onChange={set("music_offset")} fmt={fmtTime} testId="music-offset-slider" />
                  <SliderRow label="Music volume" value={settings.music_volume} min={0} max={1} step={0.05}
                    onChange={set("music_volume")} fmt={(x) => `${Math.round(x * 100)}%`} testId="music-volume-slider" />
                </>
              )}

              {video.has_audio && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase text-muted-foreground">Original audio</span>
                    <div className="flex gap-1">
                      {["mix", "mute"].map((m) => (
                        <Button key={m} size="sm" variant={settings.original_mode === m ? "default" : "outline"} className="h-7 font-mono text-[10px]"
                          onClick={() => applySettings({ original_mode: m })} data-testid={`orig-mode-${m}`}>
                          {m === "mix" ? "Keep & mix" : "Mute"}
                        </Button>
                      ))}
                    </div>
                  </div>
                  {settings.original_mode !== "mute" && (
                    <SliderRow label="Original audio volume" value={settings.original_volume} min={0} max={1} step={0.05}
                      onChange={set("original_volume")} fmt={(x) => `${Math.round(x * 100)}%`} testId="orig-volume-slider" />
                  )}
                </>
              )}

              {music && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <SliderRow label="Fade in" value={settings.fade_in} min={0} max={10} step={0.5}
                      onChange={set("fade_in")} fmt={(x) => `${x}s`} testId="fade-in-slider" />
                    <SliderRow label="Fade out" value={settings.fade_out} min={0} max={10} step={0.5}
                      onChange={set("fade_out")} fmt={(x) => `${x}s`} testId="fade-out-slider" />
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-2">
                    <label className="flex items-center gap-2 font-mono text-[11px] text-card-foreground">
                      <Switch checked={settings.loop_music} onCheckedChange={(v) => applySettings({ loop_music: v })} data-testid="loop-switch" />
                      Loop music if shorter than video
                    </label>
                    {video.has_audio && settings.original_mode !== "mute" && (
                      <label className="flex items-center gap-2 font-mono text-[11px] text-card-foreground">
                        <Switch checked={settings.duck_music} onCheckedChange={(v) => applySettings({ duck_music: v })} data-testid="duck-switch" />
                        Lower music during speech (applied on export)
                      </label>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* STEP 4 — preview + export */}
          <Card className="border-border/60">
            <CardContent className="p-4 space-y-3">
              <p className="font-mono text-xs font-bold text-primary">4 · Preview &amp; export</p>
              <video
                ref={videoRef}
                src={`${API}/funtime/file/${video.file_id}`}
                controls
                playsInline
                crossOrigin="use-credentials"
                onPlay={onVideoPlay}
                onPause={onVideoPause}
                onSeeked={syncMusic}
                className="w-full rounded-lg bg-black max-h-72"
                data-testid="preview-video"
              />
              {musicUrl && (
                <audio ref={musicRef} src={musicUrl} preload="auto" crossOrigin="use-credentials" data-testid="preview-music" />
              )}
              <p className="font-mono text-[10px] text-muted-foreground" data-testid="preview-time">
                Playhead {fmtTime(curTime)} / {fmtTime(settings.video_end)} · video + music play together — what you hear is what exports
              </p>

              <div className="flex flex-wrap items-end gap-2 pt-1">
                <div className="flex-1 min-w-[140px]">
                  <label className="block font-mono text-[10px] uppercase text-muted-foreground mb-1">Output file name</label>
                  <Input value={outputName} onChange={(e) => setOutputName(e.target.value)} className="h-8 font-mono text-xs" data-testid="output-name-input" />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase text-muted-foreground mb-1">Aspect</label>
                  <select value={settings.aspect} onChange={(e) => applySettings({ aspect: e.target.value })} className="h-8 px-2 rounded-md border border-border bg-background font-mono text-xs" data-testid="aspect-select">
                    <option value="original">Original</option>
                    <option value="16:9">16:9</option>
                    <option value="1:1">1:1</option>
                    <option value="9:16">9:16</option>
                  </select>
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase text-muted-foreground mb-1">Resolution</label>
                  <select value={settings.resolution} onChange={(e) => applySettings({ resolution: e.target.value })} className="h-8 px-2 rounded-md border border-border bg-background font-mono text-xs" data-testid="resolution-select">
                    <option value="original">{`Original (${video.height}p)`}</option>
                    {video.height > 1080 && <option value="1080">1080p</option>}
                    {video.height > 720 && <option value="720">720p</option>}
                  </select>
                </div>
                <Button onClick={startExport} disabled={!canExport} className="btn-3d h-8 font-mono text-xs" data-testid="export-btn">
                  {job?.status === "processing" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                  Export MP4
                </Button>
              </div>

              {job && job.status === "processing" && (
                <div className="space-y-1" data-testid="export-progress-box">
                  <div className="flex justify-between font-mono text-[10px]">
                    <span className="text-muted-foreground">Processing… {job.progress}%</span>
                    <button onClick={cancelExport} className="text-rose-400 flex items-center gap-1" data-testid="export-cancel"><XCircle className="w-3 h-3" />Cancel</button>
                  </div>
                  <div className="h-2 rounded bg-secondary overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${job.progress}%` }} /></div>
                </div>
              )}
              {job?.status === "done" && (
                <Button onClick={downloadExport} disabled={downloading} className="w-full h-9 bg-emerald-500 text-black hover:bg-emerald-400 font-mono text-xs font-bold" data-testid="download-btn">
                  <Download className="w-4 h-4 mr-1" /> Download {outputName || "video-with-music"}.mp4
                </Button>
              )}
              {job?.status === "error" && (
                <p className="font-mono text-[10px] text-rose-400" data-testid="export-error">{job.error || "Export failed."} — adjust settings and press Export to retry.</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      </>
      )}
    </div>
  );
}
